import { useEffect, useId, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { api } from '../lib/api';
import type { Cie10SearchResult } from '../lib/types';

interface Cie10ComboboxProps {
  code: string;
  description: string;
  disabled?: boolean;
  onSelect: (result: Cie10SearchResult) => void;
}

export function Cie10Combobox({ code, description, disabled = false, onSelect }: Cie10ComboboxProps) {
  const listboxId = useId();
  const requestSequence = useRef(0);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Cie10SearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    if (query.trim().length < 2 || disabled) {
      setResults([]);
      setOpen(false);
      setLoading(false);
      setError(null);
      return;
    }

    const sequence = ++requestSequence.current;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const found = await api<Cie10SearchResult[]>(
          `/api/catalogs/cie10/search?q=${encodeURIComponent(query.trim())}&limit=12`,
        );
        if (requestSequence.current !== sequence) return;
        setResults(found);
        setActiveIndex(found.length ? 0 : -1);
        setOpen(true);
      } catch (searchError) {
        if (requestSequence.current !== sequence) return;
        setResults([]);
        setOpen(true);
        setError(searchError instanceof Error ? searchError.message : 'No fue posible consultar el catálogo CIE-10.');
      } finally {
        if (requestSequence.current === sequence) setLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [disabled, query]);

  const choose = (result: Cie10SearchResult) => {
    onSelect(result);
    setQuery('');
    setResults([]);
    setOpen(false);
    setActiveIndex(-1);
  };

  const selectedLabel = code && description ? `${code} · ${description}` : '';

  return (
    <div className="cie10-combobox">
      <div className="cie10-search-input">
        <Search size={16} aria-hidden="true" />
        <input
          role="combobox"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-expanded={open}
          aria-activedescendant={open && activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined}
          disabled={disabled}
          value={query}
          placeholder={selectedLabel || 'Escriba código o nombre del diagnóstico'}
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => results.length && setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
          onKeyDown={(event) => {
            if (!open || !results.length) return;
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setActiveIndex((current) => (current + 1) % results.length);
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setActiveIndex((current) => (current - 1 + results.length) % results.length);
            } else if (event.key === 'Enter' && activeIndex >= 0) {
              event.preventDefault();
              const activeResult = results[activeIndex];
              if (activeResult) choose(activeResult);
            } else if (event.key === 'Escape') {
              setOpen(false);
            }
          }}
        />
      </div>
      {open ? (
        <div className="cie10-options" id={listboxId} role="listbox">
          {loading ? <div className="cie10-option-state">Buscando…</div> : null}
          {!loading && error ? <div className="cie10-option-state cie10-option-error">{error}</div> : null}
          {!loading && !error && !results.length ? <div className="cie10-option-state">No se encontraron coincidencias.</div> : null}
          {!loading && !error ? results.map((result, index) => (
            <button
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              className={index === activeIndex ? 'active' : ''}
              id={`${listboxId}-option-${index}`}
              key={result.code}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => choose(result)}
            >
              <strong>{result.code}</strong>
              <span>{result.description}</span>
            </button>
          )) : null}
        </div>
      ) : null}
      {selectedLabel ? <small>Seleccionado: {selectedLabel}</small> : null}
    </div>
  );
}
